import { getClassDisplayName, getClassOptionLabel, getClassInstitutionName } from "../../utils/classPresentation";
import React, { useEffect, useMemo, useState } from "react";
import { FiCalendar, FiCheck, FiPlus } from "react-icons/fi";
import MenuLateral from "../../Components/MenuLateral";
import CadastroPageHeader, { CadastroPanelHeading } from "../../Components/CadastroPageHeader";
import api from "../../services/api";
import { showApiError } from "../../services/showApiError";
import CepAddressLookup from "../../Components/CepAddressLookup";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import FieldLabel from "../../Components/FieldLabel";
import Swal from "sweetalert2";
import "./index.css";
import "../../styles/cadastro-pages.css";

const dateTimeToISOString = (date, time = "00:00") => {
  if (!date) return undefined;

  const normalizedTime = time || "00:00";
  return new Date(`${date}T${normalizedTime}:00`).toISOString();
};

const eventTypeLabels = {
  graduation: "Formatura",
  school_memory: "Recordação escolar",
  personal_photos: "Fotos pessoais",
  other: "Outros",
};

const eventStatusLabels = {
  scheduled: "Agendado",
  in_progress: "Em andamento",
  completed: "Concluido",
  cancelled: "Cancelado",
};

function CadEvento() {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [eventType, setEventType] = useState("graduation");
  const [eventDate, setEventDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [peopleQuantity, setPeopleQuantity] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [venueName, setVenueName] = useState("");
  const [venueZipCode, setVenueZipCode] = useState("");
  const [venueStreet, setVenueStreet] = useState("");
  const [venueNumber, setVenueNumber] = useState("");
  const [venueComplement, setVenueComplement] = useState("");
  const [venueDistrict, setVenueDistrict] = useState("");
  const [venueCity, setVenueCity] = useState("");
  const [venueUf, setVenueUf] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");

  const [instituicoes, setInstituicoes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedPhotographers, setSelectedPhotographers] = useState([]);

  const [isInstituicoesModalOpen, setIsInstituicoesModalOpen] = useState(false);
  const [isClassesModalOpen, setIsClassesModalOpen] = useState(false);
  const [isPhotographersModalOpen, setIsPhotographersModalOpen] = useState(false);
  const [instituicaoQuery, setInstituicaoQuery] = useState("");
  const [classeQuery, setClasseQuery] = useState("");
  const [photographerQuery, setPhotographerQuery] = useState("");

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [gridSearch, setGridSearch] = useState("");

  const aplicarEnderecoDoLocalPorCep = (address) => {
    setVenueStreet(address.street);
    setVenueDistrict(address.district);
    setVenueCity(address.city);
    setVenueUf(address.uf);
  };

  const carregarInstituicoes = async () => {
    try {
      const resp = await api.get("/institution");
      setInstituicoes(resp.data.institutions || []);
    } catch (err) {
      setInstituicoes([]);
      showApiError(err, "Erro ao carregar instituições");
    }
  };

  const carregarClasses = async () => {
    try {
      const resp = await api.get("/class");
      setClasses(resp.data.classes || []);
    } catch (err) {
      setClasses([]);
      showApiError(err, "Erro ao carregar turmas");
    }
  };

  const carregarFotografos = async () => {
    try {
      const resp = await api.get("/users", {
        params: {
          role: "PHOTOGRAPHER",
          limit: 100,
        },
      });

      const users = resp.data?.found?.users || resp.data?.users || [];
      setPhotographers(users.filter((user) => user.role === "PHOTOGRAPHER"));
    } catch (err) {
      setPhotographers([]);
      showApiError(err);
    }
  };

  const carregarEventos = async () => {
    setLoading(true);
    try {
      const resp = await api.get("/events", {
        params: { limit: 100 },
      });
      const eventos = (resp.data.data || resp.data.events || []).filter(
        (evento) => evento.active !== false && !evento.deletedAt,
      );
      const selectedClassIds = new Set(selectedClasses.map((item) => item.id));

      const filtrados = eventos.filter((evento) => {
        const eventClasses = evento.classes || [];
        const eventClassIds = evento.classIds || eventClasses.map((item) => item.id);

        const matchesInstitution = selectedInstitution
          ? eventClasses.some((item) => item.institution?.id === selectedInstitution.id)
          : true;

        const matchesClasses = selectedClassIds.size > 0
          ? eventClassIds.some((classId) => selectedClassIds.has(classId))
          : true;

        return matchesInstitution && matchesClasses;
      });

      setLista(filtrados);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarInstituicoes();
    carregarClasses();
    carregarFotografos();
    // Estas cargas ocorrem apenas na montagem da pagina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregarEventos();
    // Instituicao e turmas selecionadas sao os gatilhos completos da carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitution, selectedClasses]);

  const limpar = () => {
    setId("");
    setName("");
    setEventType("graduation");
    setEventDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
    setPeopleQuantity("");
    setDescription("");
    setLocation("");
    setVenueName("");
    setVenueZipCode("");
    setVenueStreet("");
    setVenueNumber("");
    setVenueComplement("");
    setVenueDistrict("");
    setVenueCity("");
    setVenueUf("");
    setCoverFile(null);
    setCoverPreview("");
    setSelectedPhotographers([]);
  };

  const abrirNovoCadastro = () => {
    limpar();
    setShowForm(true);
  };

  const fecharFormulario = () => {
    limpar();
    setShowForm(false);
  };

  const selecionarCapa = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const enviarCapa = async (eventId) => {
    if (!coverFile || !eventId) return;

    const formData = new FormData();
    formData.append("cover", coverFile);

    await api.post(`/events/${eventId}/cover-photo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const salvar = async () => {
    if (selectedClasses.length === 0) {
      Swal.fire("Selecione ao menos uma turma");
      return;
    }

    if (!name || !eventDate) {
      Swal.fire("Nome e data são obrigatórios");
      return;
    }

    const payload = {
      name,
      eventType,
      description,
      location,
      eventDate: dateTimeToISOString(eventDate, startTime),
      startDate: dateTimeToISOString(eventDate, startTime),
      startTime: startTime || undefined,
      endDate: dateTimeToISOString(endDate, endTime),
      endTime: endTime || undefined,
      peopleQuantity: peopleQuantity ? Number(peopleQuantity) : undefined,
      venueName,
      venueZipCode,
      venueStreet,
      venueNumber,
      venueComplement,
      venueDistrict,
      venueCity,
      venueUf,
      classIds: selectedClasses.map((item) => item.id),
      photographerIds: selectedPhotographers.map((item) => item.id),
    };

    setLoading(true);
    try {
      let response;
      if (id) {
        response = await api.patch(`/events/${id}`, payload);
      } else {
        response = await api.post("/events", payload);
      }

      await enviarCapa(id || response.data?.id);

      Swal.fire(id ? "Evento atualizado!" : "Evento criado!");
      limpar();
      setShowForm(false);
      carregarEventos();
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const selecionar = (item) => {
    setShowForm(true);
    setId(item.id);
    setName(item.name || "");
    setEventType(item.eventType || "graduation");
    setEventDate(item.eventDate?.substring(0, 10) || "");
    setStartTime(item.startTime?.substring(0, 5) || "");
    setEndDate(item.endDate?.substring(0, 10) || "");
    setEndTime(item.endTime?.substring(0, 5) || "");
    setPeopleQuantity(item.peopleQuantity ? String(item.peopleQuantity) : "");
    setDescription(item.description || "");
    setLocation(item.location || "");
    setVenueName(item.venueName || "");
    setVenueZipCode(item.venueZipCode || "");
    setVenueStreet(item.venueStreet || "");
    setVenueNumber(item.venueNumber || "");
    setVenueComplement(item.venueComplement || "");
    setVenueDistrict(item.venueDistrict || "");
    setVenueCity(item.venueCity || "");
    setVenueUf(item.venueUf || "");
    setCoverFile(null);
    setCoverPreview(item.coverPhotoMedia?.path || "");
    setSelectedClasses(item.classes || []);
    setSelectedPhotographers(
      (item.photographers || []).filter((photographer) => photographer.role === "PHOTOGRAPHER"),
    );
    setSelectedInstitution(item.classes?.[0]?.institution || null);
  };

  const obterPreflightLifecycle = async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}/lifecycle-preflight`);
      return response.data?.preflight || response.data;
    } catch (err) {
      await showApiError(err);
      return null;
    }
  };

  const formatarBloqueiosLifecycle = (preflight) => {
    const blockers = preflight?.blockers || [];
    if (!blockers.length) return "";

    return blockers
      .map((blocker) => {
        const reasons = (blocker.reasons || []).map((reason) => {
          if (reason === "ORDER_IN_PROGRESS") return "pedido em andamento";
          if (reason === "SELECTION_IN_PROGRESS") return "seleção em andamento";
          return "estado comercial não terminal";
        });
        return `Pedido ${blocker.orderId || "-"} · item ${blocker.itemId || "-"} · pedido ${blocker.commercialStatus || "-"} · seleção ${blocker.selectionStatus || "-"}: ${reasons.join(", ")}`;
      })
      .join("<br/>");
  };

  const executarAcaoLifecycle = async (eventId, action) => {
    const preflight = await obterPreflightLifecycle(eventId);
    if (!preflight) return;

    if (preflight.blocked) {
      await Swal.fire({
        icon: "warning",
        title: "Ação bloqueada",
        html: `${preflight.message}<br/><br/>${formatarBloqueiosLifecycle(preflight)}`,
        confirmButtonText: "Entendi",
      });
      return;
    }

    const isDelete = action === "delete";
    const confirmation = await Swal.fire({
      icon: "warning",
      title: isDelete ? "Excluir logicamente?" : "Desativar evento?",
      html: isDelete
        ? "A exclusão lógica preserva fotos, seleções, pedidos e auditoria. A desativação é a ação recomendada."
        : "A desativação impede novas operações e preserva todo o histórico do evento.",
      showCancelButton: true,
      confirmButtonText: isDelete ? "Excluir logicamente" : "Desativar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation?.isConfirmed) return;

    try {
      if (isDelete) {
        await api.delete(`/events/${eventId}`);
      } else {
        await api.post(`/events/${eventId}/deactivate`);
      }
      await Swal.fire({
        icon: "success",
        title: isDelete ? "Evento excluído logicamente" : "Evento desativado",
        text: "O histórico e os vínculos foram preservados.",
      });
      await carregarEventos();
    } catch (err) {
      await showApiError(err);
    }
  };

  const excluir = (idDel) => executarAcaoLifecycle(idDel, "delete");

  const desativar = (idDel) => executarAcaoLifecycle(idDel, "deactivate");

  const selectedClassIds = useMemo(
    () => new Set(selectedClasses.map((item) => item.id)),
    [selectedClasses],
  );

  const instituicoesFiltradas = instituicoes.filter((i) =>
    i.name.toLowerCase().includes(instituicaoQuery.toLowerCase()),
  );

  const classesFiltradas = classes.filter((c) => {
    const matchesQuery = getClassOptionLabel(c).toLowerCase().includes(classeQuery.toLowerCase());
    const matchesInstitution = selectedInstitution
      ? c.institutionId === selectedInstitution.id
      : true;

    return matchesQuery && matchesInstitution;
  });

  const selectedPhotographerIds = useMemo(
    () => new Set(selectedPhotographers.map((item) => item.id)),
    [selectedPhotographers],
  );

  const photographersFiltrados = photographers.filter((photographer) => {
    const query = photographerQuery.trim().toLowerCase();
    if (!query) return true;

    return (
      (photographer.name || "").toLowerCase().includes(query) ||
      (photographer.email || "").toLowerCase().includes(query)
    );
  });

  const listaFiltrada = useMemo(() => {
    const query = gridSearch.trim().toLowerCase();
    if (!query) return lista;

    return lista.filter((item) => (item.name || "").toLowerCase().includes(query));
  }, [gridSearch, lista]);

  const resumoTurmas = (() => {
    if (selectedClasses.length === 0) return "Selecione";
    if (selectedClasses.length === 1) return getClassDisplayName(selectedClasses[0]);
    if (selectedClasses.length === 2) {
      return selectedClasses.map((item) => getClassDisplayName(item)).join(", ");
    }
    return `${getClassDisplayName(selectedClasses[0])} +${selectedClasses.length - 1}`;
  })();

  const resumoFotografos = (() => {
    if (selectedPhotographers.length === 0) return "Opcional";
    if (selectedPhotographers.length === 1) return selectedPhotographers[0].name;
    if (selectedPhotographers.length === 2) {
      return selectedPhotographers.map((item) => item.name).join(", ");
    }
    return `${selectedPhotographers[0].name} +${selectedPhotographers.length - 1}`;
  })();

  const toggleClasse = (classe) => {
    setSelectedClasses((current) => {
      if (current.some((item) => item.id === classe.id)) {
        return current.filter((item) => item.id !== classe.id);
      }
      return [...current, classe];
    });
  };

  const togglePhotographer = (photographer) => {
    setSelectedPhotographers((current) => {
      if (current.some((item) => item.id === photographer.id)) {
        return current.filter((item) => item.id !== photographer.id);
      }

      return [...current, photographer];
    });
  };

  return (
    <div className="pagina-cad cadastro-shell cadastro-shell-eventos">
      <MenuLateral />
      <div className="conteudo-cad">
        <CadastroPageHeader
          eyebrow="Cadastros / Operação"
          title="Eventos"
          description="Organize datas, locais e responsáveis para que o trabalho de fotos comece no contexto certo."
          icon={FiCalendar}
          context="Agenda operacional"
          contextDetail="Vínculos prontos para upload e publicação"
        />

        <div className="selecao-cards">
          <div className={`card-selecao ${selectedInstitution ? "ativo" : ""}`}>
            <div className="card-info">
              <span className="card-label">Instituição</span>
              <strong>{selectedInstitution?.name || "Todas as instituições"}</strong>
            </div>
            <button onClick={() => setIsInstituicoesModalOpen(true)}>Selecionar</button>
          </div>

          <div className={`card-selecao ${selectedClasses.length > 0 ? "ativo" : ""}`}>
            <div className="card-info">
              <span className="card-label">Turmas</span>
              <strong style={{ overflowWrap: "anywhere" }}>{resumoTurmas}</strong>
              {selectedClasses.length > 0 && <small>{[...new Set(selectedClasses.map(getClassInstitutionName))].join(", ")}</small>}
            </div>
            <button onClick={() => setIsClassesModalOpen(true)}>Selecionar</button>
          </div>

          <div className={`card-selecao ${selectedPhotographers.length > 0 ? "ativo" : ""}`}>
            <div className="card-info">
              <span className="card-label">Fotógrafos</span>
              <strong>{resumoFotografos}</strong>
              <small>Vincule um ou mais fotógrafos ao evento</small>
            </div>
            <button onClick={() => setIsPhotographersModalOpen(true)}>Selecionar</button>
          </div>
        </div>

        <div className="cad-toolbar">
          <button onClick={abrirNovoCadastro}>Adicionar +</button>
        </div>

        {showForm && (
        <div className="form-cad">
          <CadastroPanelHeading
            eyebrow={id ? "Edição de cadastro" : "Novo cadastro"}
            title={id ? "Atualizar evento" : "Adicionar evento"}
            description="Defina o período, o local e os vínculos que orientarão a cobertura fotográfica."
            icon={id ? FiCalendar : FiPlus}
          />
          <FieldLabel label="Nome do evento">
            <input placeholder="Nome do evento" value={name} onChange={(e) => setName(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Tipo do evento">
            <select value={eventType} onChange={(e) => setEventType(e.target.value)} style={{ height: "35px" }}>
              <option value="graduation">Formatura</option>
              <option value="school_memory">Recordação escolar</option>
              <option value="personal_photos">Fotos pessoais</option>
              <option value="other">Outros</option>
            </select>
          </FieldLabel>
          <FieldLabel label="Quantidade de pessoas">
            <input type="number" min="0" placeholder="Quantidade de pessoas" value={peopleQuantity} onChange={(e) => setPeopleQuantity(e.target.value)} />
          </FieldLabel>
          <div className="row-dates">
            <FieldLabel label="Data de início">
              <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
            </FieldLabel>
            <FieldLabel label="Hora de início">
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </FieldLabel>
          </div>
          <div className="row-dates">
            <FieldLabel label="Data de término">
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </FieldLabel>
            <FieldLabel label="Hora de término">
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </FieldLabel>
          </div>
          <FieldLabel label="Nome do local">
            <input placeholder="Nome do local" value={venueName} onChange={(e) => setVenueName(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Sala, área ou ponto de encontro">
            <input placeholder="Sala, área ou ponto de encontro" value={location} onChange={(e) => setLocation(e.target.value)} />
          </FieldLabel>
          <CepAddressLookup
            address={{
              zipCode: venueZipCode,
              street: venueStreet,
              number: venueNumber,
              complement: venueComplement,
              district: venueDistrict,
              city: venueCity,
              uf: venueUf,
            }}
            onZipCodeChange={setVenueZipCode}
            onApplyAddress={aplicarEnderecoDoLocalPorCep}
            disabled={loading}
          />
          <FieldLabel label="Logradouro">
            <input placeholder="Logradouro" value={venueStreet} onChange={(e) => setVenueStreet(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Número">
            <input placeholder="Número" value={venueNumber} onChange={(e) => setVenueNumber(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Complemento">
            <input placeholder="Complemento" value={venueComplement} onChange={(e) => setVenueComplement(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Bairro">
            <input placeholder="Bairro" value={venueDistrict} onChange={(e) => setVenueDistrict(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="Cidade">
            <input placeholder="Cidade" value={venueCity} onChange={(e) => setVenueCity(e.target.value)} />
          </FieldLabel>
          <FieldLabel label="UF">
            <input placeholder="UF" maxLength="2" value={venueUf} onChange={(e) => setVenueUf(e.target.value.toUpperCase())} />
          </FieldLabel>
          <FieldLabel label="Descrição">
            <textarea placeholder="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} />
          </FieldLabel>

          <div className="cover-field">
            <div>
              <strong>Foto de capa</strong>
              <small>Esta imagem aparece na tela de Eventos.</small>
            </div>
            <label className="cover-picker">
              {coverPreview ? (
                coverFile ? (
                  <img src={coverPreview} alt="Capa do evento" />
                ) : (
                  <AuthenticatedMedia path={coverPreview} alt="Capa do evento" />
                )
              ) : (
                <span>Selecionar foto de capa</span>
              )}
              <input type="file" accept="image/*" onChange={selecionarCapa} />
            </label>
          </div>

          <div className="botoes">
            <button onClick={salvar}>{id ? "Atualizar" : "Salvar"}</button>
            <button className="btn-limpar" onClick={fecharFormulario}>Limpar</button>
          </div>
        </div>
        )}

        <section className="cadastro-list-panel" aria-labelledby="eventos-list-title">
        <div className="cadastro-list-heading">
          <div>
            <span className="cadastro-list-kicker">Agenda cadastrada</span>
            <h2 id="eventos-list-title">Eventos registrados</h2>
            <p>Edite os dados do evento ou siga para as operações de fotos vinculadas.</p>
          </div>
          <span className="cadastro-list-count">{listaFiltrada.length} {listaFiltrada.length === 1 ? "item" : "itens"}</span>
        </div>

        {loading && <p className="cadastro-loading" role="status">Carregando eventos...</p>}

        <div className="grid-toolbar">
          <FieldLabel label="Buscar por nome">
            <input
              className="grid-search-input"
              type="text"
              placeholder="Buscar por nome"
              value={gridSearch}
              onChange={(e) => setGridSearch(e.target.value)}
            />
          </FieldLabel>
        </div>

        <div className="cadastro-table-wrap">
        <table className="grid-cad">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Data</th>
              <th>Fotógrafos</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {listaFiltrada.map((e) => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td>{eventTypeLabels[e.eventType] || e.eventType || "-"}</td>
                <td>{new Date(e.eventDate).toLocaleDateString()}</td>
                <td>{e.photographers?.map((item) => item.name).join(", ") || "-"}</td>
                <td>{eventStatusLabels[e.status] || e.status || "-"}</td>
                <td>
                  <button onClick={() => selecionar(e)}>Editar</button>
                  <button onClick={() => desativar(e.id)}>Desativar</button>
                  <button className="btn-excluir" onClick={() => excluir(e.id)}>
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        </section>
      </div>

      {isInstituicoesModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsInstituicoesModalOpen(false)}
        >
          <div
            className="modal-card modal-animate"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Selecionar Instituição</h3>
              <button
                className="modal-close"
                onClick={() => setIsInstituicoesModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <FieldLabel label="Buscar instituição">
                <input
                  className="modal-search"
                  placeholder="Buscar instituição..."
                  value={instituicaoQuery}
                  onChange={(e) => setInstituicaoQuery(e.target.value)}
                />
              </FieldLabel>

              <div className="modal-list">
                <div
                  className="modal-item"
                  onClick={() => {
                    setSelectedInstitution(null);
                    setIsInstituicoesModalOpen(false);
                  }}
                >
                  <div className="modal-item-left">
                    <strong>Todas as instituições</strong>
                    <small>Exibe turmas e eventos da empresa inteira</small>
                  </div>
                  <span className="modal-item-action">Selecionar</span>
                </div>

                {instituicoesFiltradas.length === 0 && (
                  <div className="modal-empty">
                    Nenhum resultado encontrado
                  </div>
                )}

                {instituicoesFiltradas.map((i) => (
                  <div
                    key={i.id}
                    className="modal-item"
                    onClick={() => {
                      setSelectedInstitution(i);
                      setIsInstituicoesModalOpen(false);
                    }}
                  >
                    <div className="modal-item-left">
                      <strong>{i.name}</strong>
                      <small>ID: {i.id}</small>
                    </div>
                    <span className="modal-item-action">
                      Selecionar
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isClassesModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsClassesModalOpen(false)}
        >
          <div
            className="modal-card modal-animate"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Selecionar Turmas</h3>
              <button
                className="modal-close"
                onClick={() => setIsClassesModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <FieldLabel label="Buscar turma">
                <input
                  className="modal-search"
                  placeholder="Buscar turma..."
                  value={classeQuery}
                  onChange={(e) => setClasseQuery(e.target.value)}
                />
              </FieldLabel>

              <div className="modal-list">
                {classesFiltradas.length === 0 && (
                  <div className="modal-empty">
                    Nenhuma turma encontrada
                  </div>
                )}

                {classesFiltradas.map((c) => {
                  const isSelected = selectedClassIds.has(c.id);

                  return (
                    <div
                      key={c.id}
                      className="modal-item"
                      onClick={() => toggleClasse(c)}
                    >
                      <div className="modal-item-left">
                        <strong style={{ overflowWrap: "anywhere" }}>{getClassDisplayName(c)}</strong>
                        <small>
                          {c.institution?.name || "Instituição não informada"}
                        </small>
                      </div>

                      <button
                        type="button"
                        className={`modal-item-action modal-item-action-button${isSelected ? " is-selected" : ""}`}
                        aria-label={`${isSelected ? "Remover" : "Selecionar"} turma ${getClassOptionLabel(c)}`}
                        aria-pressed={isSelected}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleClasse(c);
                        }}
                      >
                        {isSelected ? <FiCheck aria-hidden="true" /> : null}
                        {isSelected ? "Selecionada" : "Selecionar"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <footer className="modal-footer">
              <button
                type="button"
                className="modal-footer-action"
                onClick={() => setIsClassesModalOpen(false)}
              >
                Buscar
              </button>
            </footer>
          </div>
        </div>
      )}

      {isPhotographersModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsPhotographersModalOpen(false)}
        >
          <div
            className="modal-card modal-animate"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Selecionar Fotógrafos</h3>
              <button
                className="modal-close"
                onClick={() => setIsPhotographersModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <FieldLabel label="Buscar fotógrafo">
                <input
                  className="modal-search"
                  placeholder="Buscar fotógrafo..."
                  value={photographerQuery}
                  onChange={(e) => setPhotographerQuery(e.target.value)}
                />
              </FieldLabel>

              <div className="modal-list">
                <div
                  className="modal-item"
                  onClick={() => setSelectedPhotographers([])}
                >
                  <div className="modal-item-left">
                    <strong>Nenhum fotógrafo vinculado</strong>
                    <small>O vínculo é opcional e pode ser definido depois.</small>
                  </div>
                  <span className="modal-item-action">Limpar</span>
                </div>

                {photographersFiltrados.length === 0 && (
                  <div className="modal-empty">
                    Nenhum fotógrafo encontrado
                  </div>
                )}

                {photographersFiltrados.map((photographer) => {
                  const isSelected = selectedPhotographerIds.has(photographer.id);

                  return (
                    <div
                      key={photographer.id}
                      className="modal-item"
                      onClick={() => togglePhotographer(photographer)}
                    >
                      <div className="modal-item-left">
                        <strong>{photographer.name}</strong>
                        <small>{photographer.email}</small>
                      </div>

                      <span className="modal-item-action">
                        {isSelected ? "Remover" : "Selecionar"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CadEvento;
